import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { useOnboarding } from "@/context/OnboardingContext";
import { useStepValidation } from "@/hooks/useStepValidation";
import { OnboardingErrorModal } from "./OnboardingErrorModal";
import { debugLogger } from '@/utils/debugLogger';
import StickyOnboardingFooter from "./StickyOnboardingFooter";

const GenderStep = () => {
  const { data, updateData, nextStep } = useOnboarding();
  const { validateAndSaveStep, isValidating } = useStepValidation();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorDetails, setErrorDetails] = useState<{
    message: string;
    timestamp: string;
    step: number;
    type: string;
  } | null>(null);
  const selectedGender = data.gender;

  const handleSelectGender = (gender: string) => {
    updateData({ gender: gender as any });
  };

  const handleNext = async () => {
    const success = await validateAndSaveStep(
      data,
      {
        stepNumber: 2,
        customValidation: (stepData) => {
          if (!stepData.gender || stepData.gender.trim() === '') {
            return { isValid: false, error: 'Selecione seu gênero para continuar' };
          }
          return { isValid: true };
        },
        onSuccess: () => {
          debugLogger.debug('Gender step completed successfully');
          nextStep();
        },
        onError: (error, details) => {
          debugLogger.error('Gender step failed:', error);
          
          const safeErrorDetails = {
            message: error || 'Erro desconhecido',
            timestamp: details?.timestamp || new Date().toISOString(),
            step: details?.step || 2,
            type: details?.type || 'gender_save_failed'
          };
          
          setErrorDetails(safeErrorDetails);
          setShowErrorModal(true);
        }
      }
    );

    if (!success) {
      debugLogger.error('Gender step validation failed');
    }
  };

  const handleRetry = () => {
    setShowErrorModal(false);
    setErrorDetails(null);
    handleNext();
  };

  return (
    <>
      <StickyOnboardingFooter
        buttonText="Continuar"
        onClick={handleNext}
        disabled={!selectedGender}
        isLoading={isValidating}
        loadingText="Salvando..."
      >
        <div className="max-w-lg mx-auto">
          <div className="text-center mb-4 md:mb-8">
            <h1 className="text-xl md:text-2xl font-bold text-white mb-1 md:mb-2">
              Como você se identifica?
            </h1>
            <p className="text-xs md:text-sm text-white/70">
              Esta informação nos ajuda a personalizar sua experiência
            </p>
          </div>

          <div className="space-y-2 md:space-y-4">
            <Card 
              className={`bg-black/40 backdrop-blur-sm border-2 shadow-2xl cursor-pointer transition-all duration-300 hover:bg-black/50 ${
                selectedGender === "female" ? "border-luxury-pinkBright shadow-luxury-pinkBright/25" : "border-transparent"
              }`}
              onClick={() => handleSelectGender("female")}
              data-testid="gender-card-female"
            >
              <div className="p-4 md:p-6 flex items-center justify-between">
                <div className="flex items-center space-x-3 md:space-x-4">
                  <div className="p-2 md:p-3 rounded-full bg-pink-500/20">
                    <span className="text-xl md:text-2xl">👩</span>
                  </div>
                  <div>
                    <h3 className="text-lg md:text-xl font-medium text-white">Feminino</h3>
                    <p className="text-white/70 text-xs md:text-sm">Identifico-me como mulher</p>
                  </div>
                </div>
                <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center ${
                  selectedGender === "female" ? "bg-luxury-pinkBright border-luxury-pinkBright" : "border-white/30"
                }`}>
                  {selectedGender === "female" && (
                    <div className="h-2 w-2 rounded-full bg-white"></div>
                  )}
                </div>
              </div>
            </Card>

            <Card 
              className={`bg-black/40 backdrop-blur-sm border-2 shadow-2xl cursor-pointer transition-all duration-300 hover:bg-black/50 ${
                selectedGender === "male" ? "border-luxury-pinkBright shadow-luxury-pinkBright/25" : "border-transparent"
              }`}
              onClick={() => handleSelectGender("male")}
              data-testid="gender-card-male"
            >
              <div className="p-4 md:p-6 flex items-center justify-between">
                <div className="flex items-center space-x-3 md:space-x-4">
                  <div className="p-2 md:p-3 rounded-full bg-blue-500/20">
                    <span className="text-xl md:text-2xl">👨</span>
                  </div>
                  <div>
                    <h3 className="text-lg md:text-xl font-medium text-white">Masculino</h3>
                    <p className="text-white/70 text-xs md:text-sm">Identifico-me como homem</p>
                  </div>
                </div>
                <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center ${
                  selectedGender === "male" ? "bg-luxury-pinkBright border-luxury-pinkBright" : "border-white/30"
                }`}>
                  {selectedGender === "male" && (
                    <div className="h-2 w-2 rounded-full bg-white"></div>
                  )}
                </div>
              </div>
            </Card>
          </div>
        </div>
      </StickyOnboardingFooter>

      {/* Only render modal when it should be shown */}
      {showErrorModal && errorDetails && (
        <OnboardingErrorModal
          isOpen={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          onRetry={handleRetry}
          stepNumber={2}
          errorDetails={errorDetails}
        />
      )}
    </>
  );
};

export default GenderStep;
